import { Extension } from '@tiptap/core';
import type { JSONContent } from '@tiptap/core';
import type { Schema } from '@tiptap/pm/model';
import { activateMediaManager } from 'Shared/FrameModules/MediaLibrary/media_manager_helpers';
import { canHoldWikiImage, wikiImageContent } from './wiki_image';

declare module '@tiptap/core' {
   interface Commands<ReturnType> {
      mediaManager: {
         activateMediaManager: () => ReturnType;
      };
   }
}

type MediaItem = Awaited<ReturnType<typeof activateMediaManager>>;

// MediaItemData exposes each field as a getter function, and only for the
// fields the item actually has.
function field(mediaItem: MediaItem, name: string): unknown {
   const getter = mediaItem.data?.[name];
   return typeof getter === 'function' ? getter.call(mediaItem.data) : undefined;
}

/**
 * The picked item as an image node, or null for media the editor can't
 * show as a picture.
 */
function imageContent(mediaItem: MediaItem): JSONContent | null {
   const imageid = field(mediaItem, 'imageid');
   if (!imageid || !mediaItem.toWikiText().startsWith('[image|')) {
      return null;
   }
   return wikiImageContent(imageid as string | number, size => field(mediaItem, size));
}

/**
 * A picked document as a document link showing its title, or null for other
 * media, an editor without site links, or a document with nothing to show.
 * The title is also defaultText, so it saves as [document|id] and keeps
 * following the document's own title.
 */
function documentContent(mediaItem: MediaItem, schema: Schema): JSONContent | null {
   const documentid = field(mediaItem, 'documentid');
   // Document::getDisplayTitle, trimmed as the server trims defaultText, so
   // the editor shows what the page will and the text still matches on save.
   const title = String(field(mediaItem, 'title') || field(mediaItem, 'filename') || '').trim();
   if (
      !schema.marks.siteLink ||
      !documentid ||
      !title ||
      !mediaItem.toWikiText().startsWith('[document|')
   ) {
      return null;
   }
   return {
      type: 'text',
      text: title,
      marks: [
         {
            type: 'siteLink',
            attrs: {
               kind: 'document',
               id: String(documentid),
               options: '',
               defaultText: title,
               newWindow: false,
               href: (field(mediaItem, 'view_url') as string | undefined) ?? null,
            },
         },
      ],
   };
}

export const MediaManager = Extension.create({
   name: 'mediaManager',

   addCommands() {
      return {
         activateMediaManager:
            () =>
            ({ commands }) => {
               commands.blur();
               const editor = this.editor;

               // eslint-disable-next-line @typescript-eslint/no-floating-promises -- FIXME: Promises must be awaited, end with...
               activateMediaManager('wiki_media').then(mediaItem => {
                  const image = canHoldWikiImage(editor.schema, editor.state.selection.$from)
                     ? imageContent(mediaItem)
                     : null;
                  editor
                     .chain()
                     .focus()
                     .insertContent(
                        image ?? documentContent(mediaItem, editor.schema) ?? mediaItem.toWikiText()
                     )
                     .run();
               });
               return true;
            },
      };
   },
});
